/**
 * useMasterForm — TanStack Query mutations for create/update operations.
 *
 * Validation pipeline:
 * 1. Zod schema validation (buildZodSchema)
 * 2. Custom validate() prop
 * 3. If errors → dispatch setErrors + navigate to basicData → abort
 * 4. Call service.create or service.update
 * 5. On server validation errors → map to formSlice.errors
 * 6. On 409 → open conflict dialog
 *
 * @module hooks/useMasterForm
 * @see FMPV-MVWC-E003-S003 I-001, I-003
 */
import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMasterViewStore } from '../store/MasterViewContext';
import type { MasterPatternViewEntity } from '../types/service.types';
import type { MasterPatternViewService } from '../types/service.types';
import type { MasterPatternViewDefinition } from '../types/definition.types';
import type { MasterPatternViewActionType } from '../types/permissions.types';
import { buildZodSchema } from '../engine/validationEngine';
import { getAllFields } from '../engine/fieldEngine';
import type { ToastCallback } from './useToast';
import { useToast } from './useToast';

export interface UseMasterFormOptions<T extends MasterPatternViewEntity> {
  definition: MasterPatternViewDefinition;
  service: MasterPatternViewService<T>;
  validate?: (values: Partial<T>) => Record<string, string>;
  onCreateSuccess?: (record: T) => void;
  onUpdateSuccess?: (record: T) => void;
  toast?: ToastCallback;
  showInternalToasts?: boolean;
  /** Translation function — used for toast messages and validation error messages. */
  t?: (key: string, defaultValue?: string) => string;
  onFormSave?: (
    mode: 'creating' | 'editing',
    values: Record<string, unknown>,
    originalRecord?: T,
  ) => void | Promise<void>;
  onError?: (error: Error, action: MasterPatternViewActionType) => void;
}

const defaultMessages: Record<string, string> = {
  'validation.required': 'Este campo es requerido',
  'validation.number': 'Debe ser un número válido',
  'validation.dateFormat': 'Formato de fecha inválido (YYYY-MM-DD)',
  'validation.maxLength': 'Excede la longitud máxima permitida',
};
const defaultT = (key: string, defaultValue?: string): string => defaultValue ?? defaultMessages[key] ?? key;

/** Maps buildZodSchema's internal `validation.*` keys to the flat i18n keys used by internalTranslations. */
const VALIDATION_KEY_MAP: Record<string, { key: string; defaultValue: string }> = {
  'validation.required': { key: 'validationRequired', defaultValue: 'Este campo es requerido' },
  'validation.number': { key: 'validationNumber', defaultValue: 'Debe ser un número válido' },
  'validation.dateFormat': { key: 'validationDateFormat', defaultValue: 'Formato de fecha inválido (YYYY-MM-DD)' },
  'validation.maxLength': { key: 'validationMaxLength', defaultValue: 'Excede la longitud máxima permitida' },
};

export function useMasterForm<T extends MasterPatternViewEntity>({
  definition,
  service,
  validate,
  onCreateSuccess,
  onUpdateSuccess,
  toast: toastProp,
  showInternalToasts = true,
  t = defaultT,
  onFormSave,
  onError,
}: UseMasterFormOptions<T>) {
  const [isCustomSubmitting, setIsCustomSubmitting] = useState(false);

  const mode = useMasterViewStore((s) => s.mode);
  const values = useMasterViewStore((s) => s.values);
  const activeRecord = useMasterViewStore((s) => s.activeRecord);
  const setErrors = useMasterViewStore((s) => s.setErrors);
  const setActiveTabKey = useMasterViewStore((s) => s.setActiveTabKey);
  const closeForm = useMasterViewStore((s) => s.closeForm);
  const setData = useMasterViewStore((s) => s.setData);
  const openConflictDialog = useMasterViewStore((s) => s.openConflictDialog);
  const activeCompanyContext = useMasterViewStore((s) => s.activeCompanyContext);
  const globalValues = useMasterViewStore((s) => s.globalValues);
  const overrides = useMasterViewStore((s) => s.overrides);
  const revertedFields = useMasterViewStore((s) => s.revertedFields);
  const companyId = activeCompanyContext && activeCompanyContext !== 'global'
    ? activeCompanyContext
    : undefined;
  const { showSuccess, showError } = useToast(toastProp, showInternalToasts);

  const queryClient = useQueryClient();

  // Load record when editing/viewing
  useQuery({
    queryKey: ['masterRecord', (activeRecord as T & { id: string })?.id],
    queryFn: () => service.getById!((activeRecord as T & { id: string }).id),
    enabled: !!(service.getById && activeRecord && (mode === 'editing' || mode === 'viewing')),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const createMutation = useMutation({
    mutationFn: (payload: Partial<T>) => service.create(payload, companyId),
    onSuccess: async (record) => {
      // OJO: invalidateQueries se dispara DESPUÉS de que companyEnable resuelva (no antes,
      // como estaba) — si se invalida primero, React Query refetchea la lista mientras el
      // enable todavía está en vuelo, así que llega con isAssigned=false y el badge de
      // "compañía asignada" no aparece hasta el siguiente reload manual de la página.
      if (definition.type === 'GLOBAL' && companyId && service.companyEnable) {
        // Company context: auto-enable + close form immediately
        try {
          await service.companyEnable((record as T & { id: string }).id, companyId);
          showSuccess(t('createEnabledSuccess', 'Registro creado y habilitado para la compañía exitosamente'));
        } catch {
          showError(t('createEnabledPartialError', 'Registro creado, pero no se pudo habilitar automáticamente. Habilítelo desde la lista.'));
        }
        queryClient.invalidateQueries({ queryKey: ['masterList'] });
        closeForm();
      } else if (definition.type === 'GLOBAL' && !companyId) {
        // Global context: keep form open — CompanyEnableModal opens on top of it.
        // closeForm() is called by the modal's onClose/onConfirm handlers.
        queryClient.invalidateQueries({ queryKey: ['masterList'] });
        showSuccess(t('createSuccess', 'Registro creado exitosamente'));
      } else {
        queryClient.invalidateQueries({ queryKey: ['masterList'] });
        showSuccess(t('createSuccess', 'Registro creado exitosamente'));
        closeForm();
      }
      onCreateSuccess?.(record);
    },
    onError: (error: Error) => {
      showError(error.message || t('createError', 'Error al crear el registro'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      payload,
      initialIsActive,
    }: {
      id: string;
      payload: Partial<T>;
      initialIsActive?: unknown;
    }) => {
      if (companyId && service.getById) {
        // Company context: PATCH /overrides does not validate version server-side.
        // Pre-flight check: fetch company-scoped record and compare:
        //   1. Global version (detects concurrent global edits)
        //   2. isActive value (detects concurrent company-override edits)
        const current = await service.getById(id, companyId);
        const currentVersion = (current as any).version;
        const payloadVersion = (payload as any).version;
        if (
          currentVersion !== undefined &&
          payloadVersion !== undefined &&
          currentVersion !== payloadVersion
        ) {
          const err = new Error('Conflicto de versión') as any;
          err.status = 409;
          throw err;
        }
        const currentIsActive = (current as any).isActive;
        if (
          initialIsActive !== undefined &&
          currentIsActive !== undefined &&
          currentIsActive !== initialIsActive
        ) {
          const err = new Error('Conflicto de versión') as any;
          err.status = 409;
          throw err;
        }
      }
      // Pass explicitly reverted fields so the service can null them in the backend.
      // Only meaningful in company context for GLOBAL type — other contexts just ignore it.
      const rf = companyId && revertedFields.length > 0 ? revertedFields : undefined;
      return service.update(id, payload, companyId, rf);
    },
    onSuccess: (record) => {
      showSuccess(t('updateSuccess', 'Registro actualizado exitosamente'));
      queryClient.invalidateQueries({ queryKey: ['masterList'] });
      closeForm();
      onUpdateSuccess?.(record);
    },
    onError: (error: any) => {
      if (error?.status === 409 || error?.statusCode === 409) {
        openConflictDialog();
        return;
      }
      // Map server field errors if present
      if (error?.errors && Array.isArray(error.errors)) {
        const fieldErrors: Record<string, string> = {};
        for (const e of error.errors) {
          if (e.field) fieldErrors[e.field] = e.message;
        }
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
          setActiveTabKey('basicData');
          return;
        }
      }
      showError(error?.message || t('updateError', 'Error al actualizar el registro'));
    },
  });

  // buildZodSchema calls its `t` argument with a single `validation.*` key (no defaultValue).
  // This adapter maps those internal keys to the flat i18n keys used by internalTranslations,
  // supplying the matching Spanish default so behavior is unchanged when i18n isn't configured.
  const validationT = useCallback(
    (key: string): string => {
      const mapped = VALIDATION_KEY_MAP[key];
      return mapped ? t(mapped.key, mapped.defaultValue) : t(key);
    },
    [t],
  );

  const runValidation = useCallback(
    (vals: Record<string, unknown>): Record<string, string> => {
      const errors: Record<string, string> = {};

      // Step 1: Zod schema validation
      const schema = buildZodSchema(getAllFields(definition), validationT);
      const result = schema.safeParse(vals);
      if (!result.success) {
        for (const issue of result.error.issues) {
          const field = issue.path[0] as string;
          if (field && !errors[field]) {
            errors[field] = issue.message;
          }
        }
      }

      // Step 2: Custom validate() prop (wins over Zod for same field)
      if (validate) {
        const customErrors = validate(vals as Partial<T>);
        for (const [field, message] of Object.entries(customErrors)) {
          errors[field] = message; // overwrites Zod message for same field
        }
      }

      return errors;
    },
    [definition, validate, validationT],
  );

  const submit = useCallback(async () => {
    const errors = runValidation(values);

    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      setActiveTabKey('basicData');
      return;
    }

    setErrors({});

    // Offline persistence path — consumer owns the API call and timing.
    // Exits before createMutation/updateMutation so those paths are untouched.
    if (onFormSave) {
      setIsCustomSubmitting(true);
      try {
        await onFormSave(
          mode === 'creating' ? 'creating' : 'editing',
          values as Record<string, unknown>,
          activeRecord as T | undefined,
        );
        closeForm();
      } catch (err) {
        onError?.(err as Error, mode === 'creating' ? 'create' : 'update');
      } finally {
        setIsCustomSubmitting(false);
      }
      return;
    }

    if (mode === 'creating') {
      await createMutation.mutateAsync(values as Partial<T>);
    } else if (mode === 'editing' && activeRecord) {
      const id = (activeRecord as T & { id: string }).id;
      const payload: Partial<T> = { ...values } as Partial<T>;
      // Include version for optimistic concurrency.
      // In company context the list row may not carry version — fall back to globalValues.version
      // (populated by getById without companyId in openEditRecord).
      const version = activeRecord.version ?? (globalValues as any).version;
      if (version !== undefined) {
        (payload as any).version = version;
      }
      // Capture isActive at save-click time for company-context conflict detection
      const initialIsActive = (activeRecord as any).isActive;
      await updateMutation.mutateAsync({ id, payload, initialIsActive });
    }
  }, [
    runValidation,
    values,
    mode,
    activeRecord,
    globalValues,
    overrides,
    revertedFields,
    companyId,
    setErrors,
    setActiveTabKey,
    createMutation,
    updateMutation,
  ]);

  return {
    submit,
    isSubmitting: createMutation.isPending || updateMutation.isPending || isCustomSubmitting,
  };
}
